import type { CSSProperties } from "react";
import type { SxProps, Theme } from "@mui/material/styles";

export const ACCENT_PURPLE = "#7B49C1";

const text = (theme: Theme) => ({
  title: theme.palette.mode === "dark" ? "#F3F4F6" : "#111827",
  label: theme.palette.mode === "dark" ? "#D1D5DB" : "#374151",
  value: theme.palette.mode === "dark" ? "#F9FAFB" : "#111827",
  muted: theme.palette.mode === "dark" ? "#9CA3AF" : "#6B7280",
  placeholder: "#9CA3AF",
  closeHover: theme.palette.mode === "dark" ? "#E5E7EB" : "#6B7280",
});

export const getOptionIconBoxStyles = (theme: Theme): SxProps<Theme> => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(167, 139, 250, 0.16)"
      : "rgba(123, 73, 193, 0.08)",
  color: theme.palette.mode === "dark" ? "#C4B5FD" : ACCENT_PURPLE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const optionAvatarSx: SxProps = {
  width: 34,
  height: 34,
  flexShrink: 0,
  "& img": {
    objectFit: "cover",
    objectPosition: "center top",
  },
};

export const popoverSx: SxProps<Theme> = (theme) => ({
  "& .MuiPaper-root": {
    backgroundColor:
      theme.palette.footer?.menuDropdown?.bgColor ??
      theme.palette.background.paper,
    borderRadius: "18px",
    padding: "16px 14px",
    overflow: "hidden",
    width: "320px",
    border:
      theme.palette.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "none",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 16px 48px rgba(0,0,0,0.5)"
        : "0 8px 32px rgba(15,23,42,0.14)",
  },
});

export const headerRowStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  mb: "8px",
};

export const closeButtonStyles: SxProps<Theme> = (theme) => ({
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#9CA3AF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px",
  borderRadius: "6px",
  "&:hover": {
    color: text(theme).closeHover,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.04)",
  },
});

export const loadingContainerStyles: SxProps = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 80,
};

export const fieldsContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

export const fieldLabelStyles: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: text(theme).label,
  mb: "6px",
});

export const formControlStyles: SxProps = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

export const selectValueRowStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  minWidth: 0,
};

export const getSelectValueTextStyles = (
  theme: Theme,
  isDefault: boolean,
): SxProps<Theme> => ({
  fontSize: "14px",
  fontWeight: 500,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: isDefault ? text(theme).placeholder : text(theme).value,
});

export const selectChevronStyle: CSSProperties = {
  flexShrink: 0,
  marginLeft: "auto",
};

export const getSelectStyles = (theme: Theme): SxProps<Theme> => ({
  borderRadius: "10px",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.14)" : "#E5E7EB"
  }`,
  "& .MuiSelect-select": {
    padding: "9px 12px",
    display: "flex",
    alignItems: "center",
  },
  "&:hover": {
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.24)" : "#D1D5DB",
  },
  "&.Mui-focused": { borderColor: ACCENT_PURPLE },
  "&.Mui-disabled": { opacity: 0.6 },
});

export const getSelectMenuPaperStyles = (theme: Theme): SxProps<Theme> => ({
  maxHeight: 280,
  borderRadius: "10px",
  mt: 0.5,
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#FFFFFF",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid #E5E7EB",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 12px 32px rgba(0,0,0,0.45)"
      : "0 8px 24px rgba(15,23,42,0.12)",
  "& .MuiList-root": {
    py: 0.5,
    px: 0.5,
  },
  "& .MuiMenuItem-root": {
    overflow: "visible",
    px: 1.5,
  },
  "& .MuiMenuItem-root:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(167,139,250,0.14)"
        : "rgba(123,73,193,0.08)",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(167,139,250,0.2)"
        : "rgba(123,73,193,0.12)",
  },
});

export const menuItemStyles: SxProps = {
  whiteSpace: "normal",
  alignItems: "flex-start",
  overflow: "visible",
  py: 1.25,
  px: 1.5,
  height: "auto",
  minHeight: "auto",
};

export const menuItemContentRowStyles: SxProps = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  width: "100%",
};

export const menuItemTextStyles: SxProps<Theme> = (theme) => ({
  fontSize: "14px",
  fontWeight: 500,
  color: text(theme).value,
  lineHeight: 1.3,
});

export const menuItemDescriptionStyles: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  color: text(theme).muted,
  mt: 0.25,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
});

export const emptyStateTextStyles: SxProps = {
  fontSize: "13px",
  color: "#9CA3AF",
};

export const savingRowStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  mt: "12px",
};

export const savingTextStyles: SxProps = {
  fontSize: "12px",
  color: "#9CA3AF",
};
